/**
 * Attendance Service
 *
 * API service for attendance tracking functionality.
 */

import type {
  AbsencesSummaryResponse,
  AttendanceDetail,
  AttendanceFilters,
  CreateAttendanceRequest,
  CreateLessonSessionRequest,
  LessonSessionDetail,
  LessonSessionListItem,
  RecentAbsence,
  SessionFilters,
  UpdateAttendanceRequest,
  UpdateAttendancesRequest,
} from "../types/attendance";
import api from "./api";

// Helper to convert snake_case to camelCase
function toCamelCase(obj: Record<string, unknown>): Record<string, unknown> {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj))
    return obj.map((item) =>
      toCamelCase(item as Record<string, unknown>),
    ) as unknown as Record<string, unknown>;

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );
    result[camelKey] =
      typeof value === "object" && value !== null
        ? toCamelCase(value as Record<string, unknown>)
        : value;
  }
  return result;
}

// Helper to convert camelCase to snake_case
function toSnakeCase(obj: Record<string, unknown>): Record<string, unknown> {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj))
    return obj.map((item) =>
      toSnakeCase(item as Record<string, unknown>),
    ) as unknown as Record<string, unknown>;

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(
      /[A-Z]/g,
      (letter) => `_${letter.toLowerCase()}`,
    );
    result[snakeKey] =
      typeof value === "object" && value !== null
        ? toSnakeCase(value as Record<string, unknown>)
        : value;
  }
  return result;
}

// =====================================
// LessonSession Endpoints
// =====================================

/**
 * Get all lesson sessions (filtered by user's classes if not director)
 */
export async function getLessonSessions(
  filters?: SessionFilters,
): Promise<LessonSessionListItem[]> {
  const params = filters
    ? toSnakeCase(filters as unknown as Record<string, unknown>)
    : undefined;
  const response = await api.get("/attendance/sessions/", { params });

  let data: unknown[];
  if (Array.isArray(response.data)) {
    data = response.data;
  } else if (response.data?.results && Array.isArray(response.data.results)) {
    data = response.data.results;
  } else {
    data = [];
  }

  return data.map((item) =>
    toCamelCase(item as Record<string, unknown>),
  ) as unknown as LessonSessionListItem[];
}

/**
 * Get sessions for a specific class group
 */
export async function getSessionsByClassGroup(
  classGroupId: string,
): Promise<LessonSessionListItem[]> {
  const response = await api.get(
    `/attendance/sessions/by_class_group/?class_group=${classGroupId}`,
  );

  let data: unknown[];
  if (Array.isArray(response.data)) {
    data = response.data;
  } else if (response.data?.results && Array.isArray(response.data.results)) {
    data = response.data.results;
  } else {
    data = [];
  }

  return data.map((item) =>
    toCamelCase(item as Record<string, unknown>),
  ) as unknown as LessonSessionListItem[];
}

/**
 * Get a single lesson session with attendance details
 */
export async function getLessonSession(
  id: string,
): Promise<LessonSessionDetail> {
  const response = await api.get(`/attendance/sessions/${id}/`);
  return toCamelCase(response.data) as unknown as LessonSessionDetail;
}

/**
 * Create a new lesson session
 */
export async function createLessonSession(
  data: CreateLessonSessionRequest,
): Promise<LessonSessionDetail> {
  const snakeData = toSnakeCase(data as unknown as Record<string, unknown>);
  console.log("[attendanceService] Creating session with data:", {
    original: data,
    snakeCase: snakeData,
  });
  const response = await api.post("/attendance/sessions/", snakeData);
  console.log("[attendanceService] Session created:", response.data);
  return toCamelCase(response.data) as unknown as LessonSessionDetail;
}

/**
 * Update a lesson session
 */
export async function updateLessonSession(
  id: string,
  data: Partial<CreateLessonSessionRequest>,
): Promise<LessonSessionDetail> {
  const snakeData = toSnakeCase(data as unknown as Record<string, unknown>);
  const response = await api.patch(`/attendance/sessions/${id}/`, snakeData);
  return toCamelCase(response.data) as unknown as LessonSessionDetail;
}

/**
 * Delete a lesson session
 */
export async function deleteLessonSession(id: string): Promise<void> {
  await api.delete(`/attendance/sessions/${id}/`);
}

/**
 * Bulk update attendances for a session
 */
export async function updateSessionAttendances(
  sessionId: string,
  data: UpdateAttendancesRequest,
): Promise<LessonSessionDetail> {
  const snakeData = toSnakeCase(data as unknown as Record<string, unknown>);
  const response = await api.post(
    `/attendance/sessions/${sessionId}/update_attendances/`,
    snakeData,
  );
  return toCamelCase(response.data) as unknown as LessonSessionDetail;
}

// =====================================
// Attendance Record Endpoints
// =====================================

/**
 * Get all attendance records
 */
export async function getAttendanceRecords(
  filters?: AttendanceFilters,
): Promise<AttendanceDetail[]> {
  const params = filters
    ? toSnakeCase(filters as unknown as Record<string, unknown>)
    : undefined;
  const response = await api.get("/attendance/records/", { params });

  let data: unknown[];
  if (Array.isArray(response.data)) {
    data = response.data;
  } else if (response.data?.results && Array.isArray(response.data.results)) {
    data = response.data.results;
  } else {
    data = [];
  }

  return data.map((item) =>
    toCamelCase(item as Record<string, unknown>),
  ) as unknown as AttendanceDetail[];
}

/**
 * Get a single attendance record
 */
export async function getAttendanceRecord(
  id: string,
): Promise<AttendanceDetail> {
  const response = await api.get(`/attendance/records/${id}/`);
  return toCamelCase(response.data) as unknown as AttendanceDetail;
}

/**
 * Create a new attendance record
 */
export async function createAttendanceRecord(
  data: CreateAttendanceRequest,
): Promise<AttendanceDetail> {
  const snakeData = toSnakeCase(data as unknown as Record<string, unknown>);
  const response = await api.post("/attendance/records/", snakeData);
  return toCamelCase(response.data) as unknown as AttendanceDetail;
}

/**
 * Update an attendance record
 */
export async function updateAttendanceRecord(
  id: string,
  data: UpdateAttendanceRequest,
): Promise<AttendanceDetail> {
  const snakeData = toSnakeCase(data as unknown as Record<string, unknown>);
  const response = await api.patch(`/attendance/records/${id}/`, snakeData);
  return toCamelCase(response.data) as unknown as AttendanceDetail;
}

/**
 * Mark an attendance record as contacted
 */
export async function markAttendanceContacted(
  id: string,
): Promise<AttendanceDetail> {
  const response = await api.post(`/attendance/records/${id}/mark_contacted/`);
  return toCamelCase(response.data) as unknown as AttendanceDetail;
}

/**
 * Get absences summary by student
 */
export async function getAbsencesSummary(params?: {
  classGroup?: string;
  minAbsences?: number;
}): Promise<AbsencesSummaryResponse> {
  const snakeParams = params
    ? toSnakeCase(params as unknown as Record<string, unknown>)
    : undefined;
  const response = await api.get("/attendance/records/absences_summary/", {
    params: snakeParams,
  });
  return toCamelCase(response.data) as unknown as AbsencesSummaryResponse;
}

/**
 * Get recent absences (last 30 days)
 */
export async function getRecentAbsences(params?: {
  days?: number;
  classGroup?: string;
}): Promise<RecentAbsence[]> {
  const snakeParams = params
    ? toSnakeCase(params as unknown as Record<string, unknown>)
    : undefined;
  const response = await api.get("/attendance/records/recent_absences/", {
    params: snakeParams,
  });

  let data: unknown[];
  if (Array.isArray(response.data)) {
    data = response.data;
  } else if (response.data?.results && Array.isArray(response.data.results)) {
    data = response.data.results;
  } else {
    data = [];
  }

  return data.map((item) =>
    toCamelCase(item as Record<string, unknown>),
  ) as unknown as RecentAbsence[];
}

export default {
  // Sessions
  getLessonSessions,
  getSessionsByClassGroup,
  getLessonSession,
  createLessonSession,
  updateLessonSession,
  deleteLessonSession,
  updateSessionAttendances,
  // Attendance Records
  getAttendanceRecords,
  getAttendanceRecord,
  createAttendanceRecord,
  updateAttendanceRecord,
  markAttendanceContacted,
  getAbsencesSummary,
  getRecentAbsences,
};
